import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, ShieldCheck, ShieldAlert } from "lucide-react";
import { api, fmtDate } from "@/lib/api";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9 ]/g, " ").split(" ").filter(Boolean).join(" ");
const txt = (v) => ({ true: "Sí", false: "No" }[String(v).toLowerCase()] ?? v);
const fechaHora = (iso) => new Date(iso).toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" });

const Campo = ({ f, resp }) => {
  if (f.type === "legal_notice") return (
    <div className="mt-3 break-inside-avoid" data-testid={`legal-${f.id}`}>
      <div className="font-semibold text-sm text-slate-900">{f.title}</div>
      <p className="text-[11px] leading-relaxed text-slate-700 whitespace-pre-line mt-1 text-justify">{f.body}</p>
    </div>
  );
  const label = f.label || f.title || f.id;
  const v = resp[norm(label)];
  return (
    <div className="flex gap-2 py-1 border-b border-slate-100 text-xs break-inside-avoid">
      <span className="w-1/2 shrink-0 text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium whitespace-pre-line">{v ? txt(v) : "—"}</span>
    </div>
  );
};

const Proteccion = ({ p }) => (
  <div className={`mt-6 rounded-md border-2 p-3 text-sm break-inside-avoid ${p?.acepta ? "border-emerald-600" : "border-rose-500"}`} data-testid="proteccion-resumen">
    <div className="flex items-center gap-2 font-semibold">{p?.acepta ? <ShieldCheck className="h-4 w-4 text-emerald-700" /> : <ShieldAlert className="h-4 w-4 text-rose-600" />}
      {p?.acepta ? "Política de protección de datos ACEPTADA" : "Sin aceptación de protección de datos registrada"}</div>
    {p && <div className="text-xs text-slate-700 mt-1 space-y-0.5">
      <div>Recibido desde saludyvision.es el {fechaHora(p.fecha)}</div>
      {p.firmante && <div>Firmante 1: {p.firmante}</div>}
      {p.firmante_2 && <div>Firmante 2: {p.firmante_2}</div>}
      {p.comunicaciones && <div>Comunicaciones comerciales: {txt(p.comunicaciones)}</div>}
    </div>}
  </div>
);

export default function CuestionarioImprimir() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get(`/cuestionarios/${id}`).then((r) => setC(r.data));
  }, [id]);
  if (!c) return <div className="p-8 text-slate-400">Cargando…</div>;
  const resp = Object.fromEntries(c.respuestas.map((r) => [norm(r.pregunta), r.respuesta]));
  const secciones = c.plantilla || Object.entries(c.respuestas.reduce((a, r) => ((a[r.seccion || "General"] ||= []).push({ id: r.pregunta, label: r.pregunta }), a), {})).map(([title, fields]) => ({ title, fields }));
  return (
    <div className="bg-white min-h-screen" data-testid="cuestionario-print-page">
      <div className="max-w-[800px] mx-auto p-8">
        <div className="flex justify-between items-start no-print mb-4">
          <span className="text-sm text-slate-500">Cuestionario tal como lo envió el paciente. Elige "Guardar como PDF" para archivarlo.</span>
          <button data-testid="cuestionario-print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-md bg-[#1E3A8A] text-white px-4 py-2 text-sm"><Printer className="h-4 w-4" />Imprimir / PDF</button>
        </div>
        <div className="flex items-end gap-4 border-b border-slate-300 pb-3">
          <img src="/logo-sv.jpg" alt="Salud y Visión" className="h-16" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-slate-500">Salud y Visión · Cuestionario</div>
            <h1 className="font-[Outfit] text-xl font-bold">{c.titulo || c.codigo}</h1>
            <div className="text-xs text-slate-600">{c.patient?.nombre}{c.patient?.codigo ? ` · ${c.patient.codigo}` : ""} · recibido el {fmtDate(c.fecha)}</div>
          </div>
        </div>
        {secciones.map((s) => (
          <div key={s.title} className="mt-5">
            <h2 className="font-[Outfit] text-sm font-semibold text-slate-900 border-b-2 border-slate-800 pb-1 mb-1">{s.title}</h2>
            {s.description && <p className="text-[11px] text-slate-500 mb-1">{s.description}</p>}
            {s.fields.map((f) => <Campo key={f.id} f={f} resp={resp} />)}
          </div>
        ))}
        <Proteccion p={c.proteccion} />
      </div>
    </div>
  );
}
